/**
 * What was said in a recording whose sound was transcribed (PRD-0115 SC-013): its turns by time and
 * voice beside the player. A click plays from a turn, the turn being said is marked as the player
 * moves, and the subtitle file the player shows can be saved. Pure: the transcript, the playhead
 * and the play action arrive as props.
 */
import { Download } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { type SubtitleTrack, turnAt, voiceLabel } from '@/features/document-overlay/video-subtitles'
import type { VideoTranscriptResponse } from '@/services/api/video'
import { cn } from '@/utils/cn'
import { formatVideoTime } from '@/utils/video-file'

const COPY = 'documents.detail.video.transcript'

/** How often the marked turn follows the playhead: often enough to read along, and only this list re-renders. */
const FOLLOW_MS = 250
/** A long recording's turns are shown this many at a time. */
const PAGE = 200

/** The turn being said, read off the playhead while the list is shown. */
function useCurrentTurn(
  transcript: VideoTranscriptResponse,
  playhead: () => number | null
): number {
  const [current, setCurrent] = useState(-1)
  useEffect(() => {
    // Nothing to do while the player stands still: only a moved playhead is looked up.
    let read: number | null | undefined
    const follow = () => {
      const seconds = playhead()
      if (seconds === read) return
      read = seconds
      setCurrent(turnAt(transcript.turns, seconds))
    }
    follow()
    const timer = window.setInterval(follow, FOLLOW_MS)
    return () => window.clearInterval(timer)
  }, [transcript, playhead])
  return current
}

export function TranscriptInRecording({
  transcript,
  playhead,
  onPlayFrom,
  subtitles,
}: {
  transcript: VideoTranscriptResponse
  playhead: () => number | null
  /** Plays the recording from a time in seconds; omit while it cannot be played. */
  onPlayFrom?: (seconds: number) => void
  subtitles: SubtitleTrack | null
}) {
  const { t } = useTranslation()
  const current = useCurrentTurn(transcript, playhead)
  const [shown, setShown] = useState(PAGE)
  if (transcript.turns.length === 0) {
    return <p className="text-sm text-muted-foreground">{t(`${COPY}.noSpeech`)}</p>
  }
  return (
    <div className="flex flex-col gap-2" data-testid="video-transcript">
      {subtitles ? (
        <Button variant="outline" size="sm" className="gap-2 self-start" asChild>
          <a href={subtitles.src} download={subtitles.filename}>
            <Download className="size-3.5" aria-hidden />
            {t(`${COPY}.download`)}
          </a>
        </Button>
      ) : null}
      <ol className="max-h-96 divide-y divide-border overflow-auto rounded-lg border border-border">
        {transcript.turns.slice(0, shown).map((turn, at) => {
          const time = formatVideoTime(turn.start_ms / 1000)
          return (
            <li
              key={turn.index}
              aria-current={at === current ? 'true' : undefined}
              className={cn('flex items-start gap-2 p-2 text-sm', at === current && 'bg-primary/5')}
            >
              <Button
                variant="ghost"
                size="sm"
                className="h-6 shrink-0 px-1.5 font-mono text-xs tabular-nums"
                disabled={!onPlayFrom}
                onClick={() => onPlayFrom?.(turn.start_ms / 1000)}
                aria-label={t(`${COPY}.playFrom`, { time })}
              >
                {time}
              </Button>
              <span className="min-w-0">
                <span className="mr-1.5 text-xs font-medium text-muted-foreground">
                  {voiceLabel(turn.voice, t)}
                </span>
                <span className="text-foreground">{turn.text}</span>
              </span>
            </li>
          )
        })}
      </ol>
      {transcript.turns.length > shown ? (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => setShown(shown + PAGE)}
        >
          {t('search.showMore')}
        </Button>
      ) : null}
    </div>
  )
}
